import { occmClient } from 'utils/clients';
import { convertLocalScheduleToUtc, convertUtcScheduleToLocal } from 'utils/date';

export const AUTOSUPPORT_ENABLED_PATH = '/system/config/asupEnabled';
export const AUTOSUPPORT_SCHEDULES_PATH = '/system/asup/schedules';
export const AUTOSUPPORT_COLLECTIONS_PATH = '/system/asup/collections';
export const ASUP_ON_DEMAND = 'ASUP_ON_DEMAND';
export const ON_DEMAND_COLLECTION_PATH = `${AUTOSUPPORT_COLLECTIONS_PATH}/${ASUP_ON_DEMAND}`;

export const ASUP_WEEKLY = 'ASUP_WEEKLY';
export const ASUP_DAYS = [
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
    'SUNDAY'
];

// Older responses omit bundleType and still represent the weekly cadence.
export const isManagedWeeklySchedule = schedule => (
    (schedule?.bundleType || ASUP_WEEKLY) === ASUP_WEEKLY && ASUP_DAYS.includes(schedule?.day)
);

const DAY_NUMBER = {
    MONDAY: 1,
    TUESDAY: 2,
    WEDNESDAY: 3,
    THURSDAY: 4,
    FRIDAY: 5,
    SATURDAY: 6,
    SUNDAY: 7
};

const formatScheduleTime = (hourOfDay, minuteOfHour) => {
    const hour = hourOfDay === 24 ? 0 : hourOfDay;
    return `${String(hour).padStart(2, '0')}:${String(minuteOfHour).padStart(2, '0')}`;
};

const parseScheduleTime = time => {
    const [hours = '0', minutes = '0'] = String(time || '').split(':');
    const hour = Number(hours);
    const minute = Number(minutes);
    return {
        hour: Number.isFinite(hour) ? hour : 0,
        minute: Number.isFinite(minute) ? minute : 0
    };
};

const toAsupSchedule = (dayOfWeek, hourOfDay, minuteOfHour) => ({
    day: ASUP_DAYS[dayOfWeek - 1],
    time: formatScheduleTime(hourOfDay, minuteOfHour)
});

// The API stores weekday and clock time in UTC. The form collects browser-local time.
export const toUtcAsupSchedule = (day, time) => {
    const { hour, minute } = parseScheduleTime(time);
    const utc = convertLocalScheduleToUtc(DAY_NUMBER[day], hour, minute);
    return toAsupSchedule(utc.dayOfWeek, utc.hourOfDay, utc.minuteOfHour);
};

export const toLocalAsupSchedule = (day, time) => {
    const { hour, minute } = parseScheduleTime(time);
    const local = convertUtcScheduleToLocal(DAY_NUMBER[day], hour, minute);
    return toAsupSchedule(local.dayOfWeek, local.hourOfDay, local.minuteOfHour);
};

export const ASUP_SCHEDULE_TYPE = 'application/vnd.netapp.bxp.asupSchedule+json';
export const ASUP_SCHEDULES_TYPE = 'application/vnd.netapp.bxp.asupSchedules+json';
export const ASUP_COLLECTION_TYPE = 'application/vnd.netapp.bxp.asupCollection+json';
export const ASUP_PROBLEM_TYPE = 'application/vnd.netapp.bxp.problem+json';
export const ASUP_VERSION = '1.0';
export const ASUP_COLLECTION_TERMINAL_STATES = ['COMPLETED', 'FAILED', 'SKIPPED_NO_INTERNET'];

const scheduleHeaders = {
    headers: {
        Accept: ASUP_SCHEDULE_TYPE,
        'Content-Type': ASUP_SCHEDULE_TYPE
    }
};

export const getAutoSupportEnabled = () => occmClient.get(AUTOSUPPORT_ENABLED_PATH);

export const setAutoSupportEnabled = value => (
    occmClient.put(AUTOSUPPORT_ENABLED_PATH, { value })
);

export const getAutoSupportSchedules = () => (
    occmClient.get(AUTOSUPPORT_SCHEDULES_PATH, {
        headers: { Accept: ASUP_SCHEDULES_TYPE }
    })
);

export const createAutoSupportSchedule = ({ bundleType, day, time }) => (
    occmClient.post(
        AUTOSUPPORT_SCHEDULES_PATH,
        {
            type: ASUP_SCHEDULE_TYPE,
            version: ASUP_VERSION,
            bundleType,
            day,
            time
        },
        scheduleHeaders
    )
);

export const updateAutoSupportSchedule = (scheduleId, time) => (
    occmClient.patch(
        `${AUTOSUPPORT_SCHEDULES_PATH}/${scheduleId}`,
        { time },
        scheduleHeaders
    )
);

export const deleteAutoSupportSchedule = scheduleId => (
    occmClient.delete(
        `${AUTOSUPPORT_SCHEDULES_PATH}/${scheduleId}`,
        { headers: { Accept: ASUP_SCHEDULE_TYPE } }
    )
);

const collectionHeaders = {
    headers: {
        Accept: ASUP_COLLECTION_TYPE,
        'Content-Type': ASUP_COLLECTION_TYPE
    }
};

export const createOnDemandAutoSupport = () => (
    occmClient.post(
        AUTOSUPPORT_COLLECTIONS_PATH,
        {
            type: ASUP_COLLECTION_TYPE,
            version: ASUP_VERSION,
            bundleType: ASUP_ON_DEMAND,
            sendASUP: true
        },
        collectionHeaders
    )
);

export const getOnDemandAutoSupport = () => (
    occmClient.get(ON_DEMAND_COLLECTION_PATH, {
        headers: { Accept: ASUP_COLLECTION_TYPE }
    })
);

export const getOnDemandAutoSupportProblem = () => (
    occmClient.get(ON_DEMAND_COLLECTION_PATH, {
        headers: { Accept: ASUP_PROBLEM_TYPE }
    })
);
